import React from "react";
import Button from "./Button";
import { DropDown } from "./Inputs";

export default function Pagination({
  total = 10,
  current = 1,
  onChange,
  dotsOnly = false,
  overflow = "auto", // controls x-overflow when pages overflow
  showChevrons = true, // show prev/next chevrons
  pageCount = 3, // enforced number of numeric buttons to show
  pageSizeOptions = [],
  pageSize,
  onPageSizeChange,
  pageSizeLabel = "Per page",
  pageSizePlaceholder = "Select amount",
  showPageSize = false,
  showStatus = false,
}) {
  const pages = Array.from({ length: total }, (_, i) => i + 1);
  const click = (p) => {
    if (p < 1 || p > total || p === current) return;
    onChange?.(p);
  };
  const canShowPageSize =
    showPageSize &&
    Array.isArray(pageSizeOptions) &&
    pageSizeOptions.length > 0 &&
    typeof onPageSizeChange === "function";
  const handlePageSizeChange = (val) => {
    onPageSizeChange?.(val);
  };
  if (dotsOnly) {
    return (
      <nav className="rm-pagination" aria-label="Pagination">
        <div className="rm-pagination__footer">
          {" "}
          {canShowPageSize ? (
            <div className="rm-pagination__size">
              <DropDown
                label={pageSizeLabel}
                labelPosition="left"
                placeholder={pageSizePlaceholder}
                value={pageSize}
                options={pageSizeOptions}
                onSelect={handlePageSizeChange}
                onChange={(e) => handlePageSizeChange(e.target.value)}
              />
            </div>
          ) : null}
          <div className="rm-pagination__nav">
            <ul className="rm-pagination__dots">
              {pages.map((p) => (
                <li key={p} className="rm-pagination__dot-item">
                  <button
                    className={[
                      "rm-pagination__dot",
                      p === current ? "is-current" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    aria-label={`Page ${p}`}
                    aria-current={p === current ? "page" : undefined}
                    onClick={() => click(p)}
                  />
                </li>
              ))}
            </ul>
          </div>
          {showStatus && (
            <div className="rm-pagination__status" aria-live="polite">
              Page {current} of {total}
            </div>
          )}
        </div>
      </nav>
    );
  }
  const resolvedCount = Math.max(1, Number(pageCount) || 1);
  let start = 1;
  let end = total;
  // Ellipses removed; we only render the window of pages

  const half = Math.floor(resolvedCount / 2);
  // Compute fixed-size window (exactly `resolvedCount` visible numbers)
  start = Math.max(1, current - half);
  end = start + resolvedCount - 1;
  if (end > total) {
    end = total;
    start = Math.max(1, end - resolvedCount + 1);
  }
  // Only show ellipses if there are hidden ranges; do not force page 1/last button
  // no-op: keep API minimal, window only

  return (
    <nav
      className={`rm-pagination${
        canShowPageSize ? " rm-pagination--with-size" : ""
      }`}
      aria-label="Pagination"
    >
      <div className="rm-pagination__footer">
        <div className="rm-pagination__size">
          {canShowPageSize ? (
            <DropDown
              label={pageSizeLabel}
              labelPosition="left"
              placeholder={pageSizePlaceholder}
              value={pageSize}
              options={pageSizeOptions}
              onSelect={handlePageSizeChange}
              onChange={(e) => handlePageSizeChange(e.target.value)}
            />
          ) : null}
        </div>
        <div className="rm-pagination__nav">
          <ul className="rm-pagination__list" style={{ overflowX: overflow }}>
            {showChevrons && (
              <li className="rm-pagination__item">
                <Button
                  className={[
                    "rm-pagination__chevron",
                    "rm-pagination__btn",
                  ].join(" ")}
                  icon="first_page"
                  iconOnly
                  aria-label="First page"
                  disabled={current <= 1}
                  onClick={() => click(1)}
                />
              </li>
            )}
            {showChevrons && (
              <li className="rm-pagination__item">
                <Button
                  className={[
                    "rm-pagination__chevron",
                    "rm-pagination__btn",
                  ].join(" ")}
                  icon="chevron_left"
                  iconOnly
                  aria-label="Previous page"
                  variant={current <= 1 ? "neutral" : "neutral"}
                  disabled={current <= 1}
                  onClick={() => click(current - 1)}
                />
              </li>
            )}
            {/* No forced first/last numeric pages */}
            {/* No ellipsis: show only the window */}
            {/* Window pages (exactly resolvedCount visible numbers total) */}
            {Array.from({ length: end - start + 1 }, (_, i) => start + i).map(
              (p) => (
                <li key={p} className="rm-pagination__item">
                  <Button
                    className={[
                      "rm-pagination__btn",
                      p === current ? "is-current" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    label={String(p)}
                    variant={p === current ? "primary" : "neutral"}
                    onClick={() => click(p)}
                  />
                </li>
              ),
            )}

            {/* No right ellipsis */}

            {showChevrons && (
              <li className="rm-pagination__item">
                <Button
                  className={[
                    "rm-pagination__chevron",
                    "rm-pagination__btn",
                  ].join(" ")}
                  icon="chevron_right"
                  iconOnly
                  aria-label="Next page"
                  variant={current >= total ? "neutral" : "neutral"}
                  disabled={current >= total}
                  onClick={() => click(current + 1)}
                />
              </li>
            )}
            {showChevrons && (
              <li className="rm-pagination__item">
                <Button
                  className={[
                    "rm-pagination__chevron",
                    "rm-pagination__btn",
                  ].join(" ")}
                  icon="last_page"
                  iconOnly
                  aria-label="Last page"
                  disabled={current >= total}
                  onClick={() => click(total)}
                />
              </li>
            )}
          </ul>
        </div>
        {showStatus && (
          <div className="rm-pagination__status" aria-live="polite">
            Page {current} of {total}
          </div>
        )}
      </div>
    </nav>
  );
}
